import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonModal from "../../../components/modals/commonModel";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useState } from "react";
import CommonForm from "../../../components/form/form";
import {
  createCategory,
  createPurchaseInvoice,
  createPurchaseOrder,
  deletePurchaseOrder,
  deletePurchaseRequest,
  getPurchaseOrderList,
  updatePurchaseOrder,
} from "../../../../services/workspace/inventory.service";
import { useLocation, useNavigate } from "react-router-dom";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import PurchaseBasicDetails from "./components/purchaseBasicDetails";
import SubTotal from "../../common/invoice/components/subTotal";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultBadge from "../../../components/badge/defaultBadge";

const actions_type = {
  DELETE: "DELETE",
  EDIT: "EDIT",
  VIEW: "VIEW",
  ASSIGN: "ASSIGN",
  CLEAR: "CLEAR",
  EXPIRE: "EXPIRE",
};

const items = [{ label: "Purchase Order", path: "" }];

const customCase = {
  0: { label: "Pending", color: "text-yellow-500 text-sm", bg: "bg-yellow-500" },
  1: { label: "Delivered", color: "text-green-600 text-sm", bg: "bg-green-600" },
  2: { label: "Cancelled", color: "text-red-600 text-sm", bg: "bg-red-600" },
};

const customCases = {
  0: "Pending",
  1: "Delivered",
  2: "Cancelled",
};

const PurchaseOrder = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { purchaseRequest } = location.state || {};

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    fetchData,
    softDelete,
    // New sorting helpers
    sortingUIProps,
  } = useCommonFetch(getPurchaseOrderList, {
    dataPath: "purchaseOrder.data",
    totalPath: "purchaseOrder.meta.total",
    errorMessage: "Failed to fetch purchase order data!",
    debounceDelay: 500,
    customParams: {
      with: "vendor,createdBy,stockItems",
    },
    // Dynamic sorting configuration
    sortingOptions: [
      { label: "	Priority", field: "	priority" },
      { label: "Date", field: "created_at" },
    ],
    sortingModule: "purchaseOrder",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    // Add data transformer function
    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item, index) => ({
          ...item,
          // Add custom fields or modify existing ones
          id: {
            data: item.id,
            className: "",
            sticky: false,
            variant: "text",
          },
          uuid: {
            data: item.uuid,
            className: "",
            sticky: false,
            variant: "text",
          },
          code: {
            data: item?.code,
            className: "",
            sticky: false,
            variant: "name",
          },

          priority: {
            data:
              item.priority === 0
                ? "Low"
                : item.priority === 1
                ? "Medium"
                : "High",
            className: "",
            sticky: false,
            variant: "text",
          },
          expected_date: {
            data: item.expected_date,
            className: "",
            sticky: false,
            variant: "date",
          },
          name: {
            data: item.vendor.name,
            className: "",
            sticky: false,
            variant: "text",
          },
          created_at: {
            data: item.created_at,
            className: "",
            sticky: false,
            variant: "date",
          },

          status: {
            data: item.status,
            className: "",
            sticky: false,
            variant: "status",
            customCases: customCase,
          },
          actions: {
            data: item.id,
            className: "",
            sticky: false,
            variant: "text",
          },
          total_amount: item.total_amount,
          sub_total: item.sub_total,
          tax: item.tax,

          stockItems: item.stockItems,
        }));

        const actionButton = {
          delete: (item) => {
            handleDelete(item.uuid.data, deletePurchaseOrder, {
              successMessage: "PO deleted successfully!",
              errorMessage: "Failed to delete PO!",
            });
          },
          view: (item) => {
            openModal(item, actions_type.VIEW);

            setSelectedId({
              id: item.id.data,
              uuid: item.uuid.data,
              code: item.code.data,
              priority: item.priority.data,
              expected_date: item.expected_date.data,
              name: item.name.data,
              createdAt: item.created_at.data,
              status: item.status.data,
              vendor: item.vendor?.name,

              sub_total: item.sub_total || 0,
              tax: item.tax || 0,
              total_amount: item.total_amount || 0,
              items_amount: item.total_amount || 0, // for Amount in Words
              total_items_tax: item.tax || 0,

              stock_items: (item?.stockItems || []).map((si) => ({
                stockItems: si.stock?.name,
                quantity: si.quantity,
                unit_price: si.unit_price,
                total_amount: si.quantity * si.unit_price,
              })),
            });
          },
        };

        return {
          data: transformedData,
          action: actionButton,
        };
      }
      return { data: [], action: {} };
    },
  });

  const TableHeaderData = [
    {
      label: "Purchase Order No.",
      key: "code",
    },
    {
      label: "Vendor Name",
      key: "name",
    },
    {
      label: "Issued Date",
      key: "created_at",
    },
    {
      label: "Expected Date",
      key: "expected_date",
    },
    {
      label: "Amount",
      key: "total_amount",
    },
    {
      label: "Priority",
      key: "priority",
    },
    {
      label: "Status",
      key: "status",
    },
  ];

  const comprehensiveFields = [
    {
      name: "title",
      label: "Title",
      type: "text",
      placeholder: "Enter Title",
      required: true,
      minLength: 3,
      maxLength: 40,
    },

  ];

  const handleCancel = () => {
    setIsEditValue(null);
    closeModal();
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        purchase_order_id: selectedId?.id, 
        due_date: selectedId?.created_at,
      };

      const response = await handleCreate(payload, createPurchaseInvoice, {
        successMessage: "Purchase Invoice created successfully!",
        errorMessage: "Failed to create Purchase Invoice!",
      });

      if (response.success) {
        resetForm();
        closeModal();
        navigate("/inventory/purchase-invoice")
      }
    } catch (error) {
      console.error("Error creating Purchase Invoice:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={"flex flex-col gap-2"}>
      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        add
        onClickAdd={() =>
          navigate("/inventory/purchase-order/add", actions_type.ADD)
        }
        shorting
        sortingModule="stock"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
        breadcrumbs={items}
      />

      <div className={"flex flex-col gap-2"}>
        <CommonTable
          search={search}
          TableHeaderData={TableHeaderData}
          tabledata={tableData.data}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          action={tableData.action}
        />

        <BottomDrawer
          size="md"
          title={"Purchase Order Detail"}
          isOpen={!!selectedId}
          setIsOpen={() => setSelectedId(null)}
        >
          <div className="flex flex-col gap-4 ">
            <div className="flex flex-row justify-between items-center border-b pb-4">
              <span className="flex flex-row gap-4">
                <p>{selectedId?.code}</p>
                <DefaultBadge
                          customCases={customCases}
                          status={selectedId?.status}
                        />
              </span>
              <DefaultButton
                variant="outline"
                name="Create Invoice"
                onClick={() => openModal(null, actions_type.ADD_EDIT)}
              />
            </div>
            <PurchaseBasicDetails item={selectedId} />
            <CommonTable
              TableHeaderData={[
                { label: "Items", key: "stockItems" },
                { label: "Quantity", key: "quantity" },
                { label: "Unit Price", key: "unit_price" },
                { label: "Amount", key: "total_amount" },
              ]}
              tabledata={selectedId?.stock_items}
              loading={loading}
            />
            <SubTotal data={selectedId} />
          </div>
        </BottomDrawer>

        <CommonModal
          modalSize="sm"
          headerTitle={"Create Invoice"}
          onClose={handleCancel}
          show={action === actions_type.ADD_EDIT}
        >
          <CommonForm
            fields={comprehensiveFields}
            initialData={{}} // no pre-filled values
            onSubmit={handleSubmit}
            submitButtonText={"Submit"}
            onCancel={handleCancel}
            cancelButtonText="Cancel"
            columnsPerRow={1}
            gap="gap-4"
            rowClassName="   "
            fieldClassName="bg-white p-3 rounded shadow-sm"
          />
        </CommonModal>
      </div>
    </div>
  );
};
export default PurchaseOrder;
